import type { PartnerResponse, PartnerStatement, StatementSide } from '@palaccount/contracts';
import { AGING_BANDS, PAGE_SIZE_ALL } from '@palaccount/contracts';
import { Hourglass, Info, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DocumentActions } from '@/features/documents/document-actions';
import { usePagedState, usePageRange } from '@/lib/paging';
import { localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import { usePartnerStatement, useSendStatement } from './api';

/**
 * CRM-04 — Customers & Suppliers Statement.
 *
 * §4's four stacked cards, in order: the toolbar and the selection panel
 * (both marked not-to-print), the five summary cards, the aging breakdown,
 * and the transaction ledger. It renders inside MD-01's partner detail rather
 * than at a route of its own, which is what makes the partner combobox of
 * §5a field 1 unnecessary: the partner is the page you are standing on, and
 * BR-CRM-04-01's "exactly one partner at a time, chosen from the partners
 * list" is MD-01's own list one click earlier.
 *
 * ── WHAT THIS SCREEN MUST NOT COMPUTE ─────────────────────────────────────
 *
 * Every money figure here comes off the response. Not one of the five cards,
 * the five bands or the running balances is derived from `statement.rows`,
 * because `rows` is ONE PAGE (NFR-42) and the figures are properties of the
 * WHOLE LEDGER (BR-CRM-04-13). A card added up in the browser would report
 * whichever fifty rows happened to be on screen — the defect PAGINATION.md
 * §3 step 10 names, and the reason `GET /api/crm/leads/stats` exists next
 * door. `partner-statement.ts` computes all of it before the search and the
 * page are applied; this file draws it.
 *
 * The one arithmetic here is the aging BAR's segment widths (BR-CRM-04-10,
 * "each sized as that band's share of the total"), which are percentages of
 * a total the server also sent — a layout figure, never money.
 */

/** §5e's five colours: "green → yellow → orange → deep orange → red". */
const BAND_BAR: Record<string, string> = {
  current: 'bg-green-500',
  d1_30: 'bg-amber-400',
  d31_60: 'bg-orange-500',
  d61_90: 'bg-orange-700',
  d90_plus: 'bg-red-600',
};
const BAND_DOT: Record<string, string> = {
  current: 'bg-green-500',
  d1_30: 'bg-amber-400',
  d31_60: 'bg-orange-500',
  d61_90: 'bg-orange-700',
  d90_plus: 'bg-red-600',
};

/**
 * BR-CRM-04-15: "A document number must link to that document, opening the
 * PURCHASE SIDE in Supplier view and the SALES SIDE otherwise."
 *
 * The rule's two halves are both real now that the supplier ledger exists:
 * the customer kinds route into the sales module, the supplier kinds into
 * purchasing. It keys off the ROW's kind rather than off the view, which is
 * the same answer and is the one that cannot be wrong — a purchase invoice
 * opens the purchase invoice whatever toggle is pressed.
 */
function statementDocPath(row: PartnerStatement['rows'][number]): string {
  switch (row.kind) {
    case 'invoice':
      return `/sales/${row.id}`;
    case 'receipt':
      return `/accounting/receipts/${row.id}`;
    case 'credit_note':
      return `/sales/returns/${row.id}`;
    case 'purchase_invoice':
      return `/purchases/bills/${row.id}`;
    case 'payment':
      return `/accounting/payments/${row.id}`;
    case 'purchase_return':
      return `/purchases/returns/${row.id}`;
  }
}

/** §5f: "A row only ever carries one of debit or credit." Which one is its KIND. */
function isDebitRow(kind: PartnerStatement['rows'][number]['kind']): boolean {
  return kind === 'invoice' || kind === 'payment' || kind === 'purchase_return';
}

/** One of §5d's first four cards. The fifth is built separately — see below. */
function SummaryCard({
  label,
  caption,
  value,
  currency,
  emphasis,
}: {
  label: string;
  caption: string;
  value: string;
  currency: string;
  emphasis?: boolean;
}) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn('text-lg font-bold', emphasis && 'text-primary')}>
        <Amount value={value} currency={currency} />
      </p>
      <p className="text-xs text-muted-foreground">{caption}</p>
    </div>
  );
}

export function PartnerStatementCard({ partner }: { partner: PartnerResponse }) {
  const { t, i18n } = useTranslation();

  /**
   * BR-CRM-04-18's window and §5c's view, in component state.
   *
   * They start EMPTY and the server fills them in: `fiscal.cycle_start` and
   * the company clock's today are the tenant's own defaults, and a browser
   * that guessed a window would be reading its own clock — which EC-19-65
   * forbids outright ("no screen may read the browser's clock"). The two
   * inputs then show what came back, so the first render is already honest
   * about which period is on screen.
   */
  const [side, setSide] = useState<StatementSide | undefined>(undefined);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  /** Which currency's statement (owner decision, 30 Oct 2026); '' = the partner's own. */
  const [stCurrency, setStCurrency] = useState('');
  const paged = usePagedState(50, 'statement');
  const { page, pageSize, setPage, change } = paged;

  const filters = {
    side,
    from: from || undefined,
    to: to || undefined,
    q: q || undefined,
    currency: stCurrency || undefined,
  };
  const { data: statement } = usePartnerStatement(partner.id, filters, page, pageSize);
  const range = usePageRange(statement, pageSize);

  /**
   * THE PRINTABLE LEDGER — the whole account, not the page on screen.
   *
   * `DocumentActions` renders `payload` in the browser for Print and asks the
   * worker for PDF, and its header states the property that buys: "SAME
   * FUNCTION, so the paper in a customer's hand and the PDF in their inbox
   * are the same document." The worker re-reads the whole ledger server-side.
   * A `payload` built from `statement.rows` would therefore print PAGE ONE
   * while the PDF of the same button-press carried the account — two
   * documents with different closing balances, which is the one bug
   * `partner-statement.ts`'s header calls the worst this module could have.
   *
   * IT IS NOT A SECOND READ IN THE ORDINARY CASE. `enabled` fires only when
   * the ledger does not already fit on the page, and most accounts have
   * fewer rows than a page — when `total <= rows.length` the page IS the
   * whole ledger and this query never runs. Only a long account pays, and it
   * pays exactly what the statement cost before it was paged at all.
   *
   * `PAGE_SIZE_ALL` is the explicit, greppable opt-out (`pagination.ts`), and
   * this caller is argued for in `UNPAGED_CALLERS` beside the two others.
   */
  const needsFullLedger = statement !== undefined && statement.total > statement.rows.length;
  const { data: fullLedger } = usePartnerStatement(
    needsFullLedger ? partner.id : undefined,
    filters,
    1,
    PAGE_SIZE_ALL,
  );
  const printable = needsFullLedger ? fullLedger : statement;

  const sendStatement = useSendStatement(partner.id);
  /**
   * BR-CRM-04-17 — CRM-04 §5: "A green confirmation appears reading
   * **Statement emailed to** and the address, and **clears itself after 2.6
   * seconds**." ERR-CRM-04-06 is the double click — "the confirmation
   * restarts its 2.6-second timer" and nothing else happens — which is what
   * the cleared timeout below buys. Both asserted in send-statement.test.tsx.
   */
  const [emailedTo, setEmailedTo] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const clearTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(clearTimer.current), []);

  const onSendStatement = () => {
    setSendError(null);
    sendStatement.mutate(statement?.currency, {
      onSuccess: (result) => {
        setEmailedTo(result.emailedTo);
        clearTimeout(clearTimer.current);
        clearTimer.current = setTimeout(() => setEmailedTo(null), 2600);
      },
      onError: (error) => {
        setEmailedTo(null);
        // The cause rides along: a structured refusal (D46 — partnerHasNoEmail,
        // naming the partner) carries key+params for the Arabic reader.
        setSendError(
          localizeServerMessage(t, error instanceof Error ? error.message : String(error), error),
        );
      },
    });
  };

  /**
   * GEN-116's question, answered by the server rather than by the browser:
   * `conversionNote` is null exactly when no conversion happened, so it is the
   * one flag that decides whether the disclosure, the two base columns and the
   * base closing figure exist at all. A base-currency customer gets none of
   * them — and gets a statement that never says the word "currency".
   */
  const converted = typeof statement?.conversionNote === 'string';
  const currency = statement?.currency ?? partner.currency;
  const shown = statement?.side ?? (partner.isCustomer ? 'customer' : 'supplier');

  // The five bands, and the total the bar divides itself by (BR-CRM-04-10).
  const bands = statement?.aging ?? AGING_BANDS.map((band) => ({ band, amount: '0.00' }));
  const bandTotal = bands.reduce((sum, band) => sum + Number(band.amount), 0);
  const overdue = Number(statement?.summary?.overdueAmount ?? '0');

  return (
    <div className="grid gap-4">
      {/*
        §4: "The first two carry a 'do not print' marker, so a printed
        statement starts at the summary and does not carry the toolbar or the
        partner picker." BR-CRM-04-16 is the rule. `print:hidden` is that
        marker — and note it is belt to the PDF path's braces: the printed
        sheet is the RENDERED statement document, which starts at the summary
        by construction, so this covers a browser Print of the page itself.
      */}
      <Card className="print:hidden">
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CardTitle>{t('partners.statement')}</CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            {emailedTo ? (
              // §5's exact sentence, and the address it names is the one the
              // SERVER used — not the one on screen, which could be stale.
              <span role="status" className="text-sm font-medium text-green-700 dark:text-green-400">
                {t('partners.statementEmailed', { address: emailedTo })}
              </span>
            ) : null}
            {sendError ? (
              <span role="alert" className="text-sm text-destructive">
                {sendError}
              </span>
            ) : null}
            {/* ERR-CRM-04-04 was the prototype defect — "Clicked Excel or
                PDF: nothing happens, neither is wired". Not reproduced: PDF
                is wired here through the documents seam, and `side` is the
                one on screen, so the paper is the view being read
                (BR-CRM-04-02/-15). There is no Excel button to leave dead. */}
            <DocumentActions
              request={{
                kind: 'partner_statement',
                locale: i18n.language.startsWith('ar') ? 'ar' : 'en',
                partnerId: partner.id,
                side: shown,
                /*
                 * BR-CRM-04-18 on paper. Sending the window the reader is
                 * looking at is what stops the printed sheet being a
                 * different period — and therefore a different closing
                 * balance — from the screen it was printed from.
                 */
                ...(statement
                  ? { from: statement.from, to: statement.to, currency: statement.currency }
                  : {}),
              }}
              payload={
                printable
                  ? {
                      kind: 'partner_statement',
                      data: {
                        side: shown,
                        partnerCode: partner.code,
                        partnerName: partner.name,
                        currency: printable.currency,
                        asOf: printable.to,
                        openingBalance: printable.summary.openingBalance,
                        // The WHOLE ledger — see `printable` above.
                        rows: printable.rows,
                        closingBalance: printable.summary.closingBalance,
                      },
                    }
                  : null
              }
            />
            <Button
              variant="outline"
              size="sm"
              disabled={sendStatement.isPending}
              onClick={onSendStatement}
            >
              <Send aria-hidden /> {t('partners.sendStatement')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/*
            §5a field 6: "Search this statement — filters the ledger rows.
            Matches the document number or the reference, ignoring capital
            letters. Placeholder: Search this statement…"

            IT IS A SERVER SEARCH, and that is the whole point of §5g. A
            browser filter over `rows` would filter the PAGE, so a match on
            page 3 would simply not exist — and the totals beside it would
            still be the account's, which reads as the search having found
            nothing. `change()` resets to page 1 (BR-DSH-09-19) so the first
            match is on the first page.
          */}
          <Input
            type="search"
            aria-label={t('partners.stSearch')}
            placeholder={t('partners.stSearch')}
            value={q}
            onChange={(event) => change(setQ)(event.target.value)}
            className="max-w-xs"
          />
        </CardContent>
      </Card>

      {/* §4's second not-to-print card: the view, the period, the currency. */}
      <Card className="print:hidden">
        <CardContent className="flex flex-wrap items-end gap-4">
          {/*
            BR-CRM-04-03: "The view toggle must appear only when the partner
            is both a customer and a supplier." ERR-CRM-04-03 is the reader's
            half — "Looked for the view toggle on a customer-only partner: it
            is not drawn, because there is nothing to switch between."

            `canToggle` is the SERVER's answer, so the control and the ledger
            under it are decided by one read. §5a field 2 fixes both captions:
            Customer view is "What they owe us", Supplier view "What we owe
            them".
          */}
          {statement?.canToggle ? (
            <div className="grid gap-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('partners.stView')}
              </span>
              <div className="flex gap-2" role="group" aria-label={t('partners.stView')}>
                {(['customer', 'supplier'] as const).map((option) => (
                  <Button
                    key={option}
                    type="button"
                    size="sm"
                    variant={shown === option ? 'default' : 'outline'}
                    aria-pressed={shown === option}
                    onClick={() => change(setSide)(option)}
                    className="flex-col items-start gap-0 py-6"
                  >
                    <span>{t(`partners.stView_${option}`)}</span>
                    <span className="text-xs font-normal opacity-80">
                      {t(`partners.stViewSub_${option}`)}
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          ) : null}

          {/*
            One statement per currency (owner decision, 30 Oct 2026): a
            partner with documents in more than one currency gets a switcher
            drawn from the server's `currencies`, the partner's own first.
            Like the view toggle, it is not drawn when there is nothing to
            switch between.
          */}
          {statement && statement.currencies.length > 1 ? (
            <div className="grid gap-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('reports.stStatementCurrency')}
              </span>
              <div
                className="flex flex-wrap gap-2"
                role="group"
                aria-label={t('reports.stStatementCurrency')}
                data-testid="statement-currency"
              >
                {statement.currencies.map((code) => (
                  <Button
                    key={code}
                    type="button"
                    size="sm"
                    dir="ltr"
                    variant={currency === code ? 'default' : 'outline'}
                    aria-pressed={currency === code}
                    onClick={() => change(setStCurrency)(code)}
                  >
                    {code}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}

          {/*
            §5a fields 3 and 4, and the prototype defect they close: "The
            period does not filter anything… the ledger renders every row the
            partner has, whatever the dates say." BR-CRM-04-18 is the rule and
            the SERVER enforces it — these two inputs only say which window to
            ask for, which is why ERR-CRM-04-05 ("the ledger does not change")
            is no longer reachable.
          */}
          <div className="grid gap-1">
            <label
              className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
              htmlFor="statement-from"
            >
              {t('partners.stFrom')}
            </label>
            <Input
              id="statement-from"
              type="date"
              dir="ltr"
              className="w-40"
              value={from || statement?.from || ''}
              onChange={(event) => change(setFrom)(event.target.value)}
            />
          </div>
          <div className="grid gap-1">
            <label
              className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
              htmlFor="statement-to"
            >
              {t('partners.stTo')}
            </label>
            <Input
              id="statement-to"
              type="date"
              dir="ltr"
              className="w-40"
              value={to || statement?.to || ''}
              onChange={(event) => change(setTo)(event.target.value)}
            />
          </div>

          {/* §5a field 5 (a read-only currency tile) is deliberately NOT drawn:
              the owner's review (bugs: Customers › statement) asked for it to
              go — a box nobody can act on. BR-CRM-04-14 still holds where it
              matters: every figure below is in the partner's own currency and
              each amount carries its symbol. */}
        </CardContent>
      </Card>

      {/*
        F-27's exclusion is said out loud by the currency switcher above:
        every currency left out of this statement is a button there, so the
        server's `excludedCurrencies` sentences are not repeated as alerts.
      */}

      {/* §4's third card — §5d's five summary figures. */}
      <Card>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <SummaryCard
            label={t('partners.stOpening')}
            caption={t('partners.stAsOf', { date: statement?.from ?? '' })}
            value={statement?.summary?.openingBalance ?? '0.00'}
            currency={currency}
          />
          <SummaryCard
            label={t('partners.stTotalDebits')}
            caption={t('partners.stThisPeriod')}
            value={statement?.summary?.totalDebits ?? '0.00'}
            currency={currency}
          />
          <SummaryCard
            label={t('partners.stTotalCredits')}
            caption={t('partners.stThisPeriod')}
            value={statement?.summary?.totalCredits ?? '0.00'}
            currency={currency}
          />
          <SummaryCard
            label={t('partners.stClosing')}
            caption={t('partners.stPeriodTo', { date: statement?.to ?? '' })}
            value={statement?.summary?.closingBalance ?? '0.00'}
            currency={currency}
            emphasis
          />
          {/*
            §5d: "The fifth card is NOT one of the four. It is built
            separately and carries its own colour: red when anything is
            overdue, green when nothing is."

            BR-CRM-04-08 is that sentence as a rule, and it has two halves —
            the colour AND the caption: "When it is zero the caption changes
            to **Nothing overdue** and the whole tile turns green, which is
            the one place on the screen that rewards a clean account."

            The test is `> 0`, on the SERVER's overdue figure, which is
            BR-CRM-04-07's sum of the four non-Current bands.
          */}
          <div
            data-testid="overdue-tile"
            className={cn(
              'rounded-lg border p-3',
              overdue > 0
                ? 'border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950'
                : 'border-green-300 bg-green-50 dark:border-green-900 dark:bg-green-950',
            )}
          >
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('partners.stOverdue')}
            </p>
            <p
              className={cn(
                'text-lg font-bold',
                overdue > 0
                  ? 'text-red-700 dark:text-red-300'
                  : 'text-green-700 dark:text-green-300',
              )}
            >
              <Amount value={statement?.summary?.overdueAmount ?? '0.00'} currency={currency} />
            </p>
            <p className="text-xs text-muted-foreground">
              {overdue > 0 ? t('partners.stThisPeriod') : t('partners.stNothingOverdue')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* §4's fourth card — §5e's aging breakdown. */}
      <Card>
        <CardHeader className="flex-row items-center gap-2 space-y-0">
          <Hourglass className="size-4 text-muted-foreground" aria-hidden />
          <CardTitle>{t('partners.stAging')}</CardTitle>
          <span className="text-sm text-muted-foreground">{t('partners.stAgingSub')}</span>
        </CardHeader>
        <CardContent className="grid gap-4">
          {/*
            BR-CRM-04-10: "The stacked aging bar must emit a segment ONLY for
            bands above zero, each sized as that band's share of the total."
            §4 says why in plain words: "a partner with nothing in 61–90 gets
            no sliver there rather than a hairline."

            One 12-pixel bar (§4: "a single 12-pixel stacked bar"). The widths
            are percentages of a total the server sent — the only arithmetic
            on this screen, and it is layout, not money.
          */}
          <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
            {bandTotal > 0
              ? bands
                  .filter((band) => Number(band.amount) > 0)
                  .map((band) => (
                    <div
                      key={band.band}
                      data-testid={`aging-seg-${band.band}`}
                      className={BAND_BAR[band.band]}
                      style={{ width: `${(Number(band.amount) / bandTotal) * 100}%` }}
                    />
                  ))
              : null}
          </div>
          {/*
            BR-CRM-04-09: "exactly five bands", and BR-CRM-04-11: "A band with
            no money must STILL RENDER ITS CARD, with the amount greyed." So
            the map is over the response's five entries — which the server
            builds from `AGING_BANDS` and never from the rows — and an empty
            band loses its colour, not its card.
          */}
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {bands.map((band) => {
              const empty = Number(band.amount) === 0;
              return (
                <div key={band.band} data-testid={`aging-band-${band.band}`} className="grid gap-1">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <span
                      aria-hidden
                      className={cn('size-2 rounded-full', empty ? 'bg-muted' : BAND_DOT[band.band])}
                    />
                    {t(`partners.stBand_${band.band}`)}
                  </span>
                  <span className={cn('text-sm font-semibold', empty && 'text-muted-foreground')}>
                    <Amount value={band.amount} currency={currency} />
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* §4's fifth card — the transaction ledger. */}
      <Card>
        <CardHeader>
          <CardTitle>{t('partners.stLedger')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          {/* GEN-115, "directly above the numbers": the disclosure names
              the target currency AND the basis of the rates. There is no
              single "rates as of" date to give — that is the whole point of
              EC-19-10, where every document carries the rate frozen when it
              was confirmed — so the sentence names the basis instead of
              inventing a date that would be false for all but one row. The
              SERVER composes it, because the server is what knows whether
              any conversion happened at all.

              GEN-116: null when nothing converted, and a base-currency
              customer therefore sees no line, no extra columns and no
              second total — ADM-01's rule that a single-currency business
              "should never see the word currency".

              GEN-117: informational grey with an info icon. Never amber,
              which on these screens means BR-MD-11-14's stale-rate warning
              and is a different kind of statement entirely. */}
          {converted ? (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Info className="size-3.5 shrink-0" aria-hidden />
              {statement!.conversionNote}
            </p>
          ) : null}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('common.date')}</TableHead>
                <TableHead>{t('partners.stKindCol')}</TableHead>
                <TableHead>{t('partners.stCode')}</TableHead>
                <TableHead>{t('partners.stReference')}</TableHead>
                <TableHead className="text-end">{t('partners.stDebit')}</TableHead>
                <TableHead className="text-end">{t('partners.stCredit')}</TableHead>
                {converted ? (
                  <>
                    <TableHead className="text-end">
                      {t('partners.stDebitBase', { currency: statement!.baseCurrency })}
                    </TableHead>
                    <TableHead className="text-end">
                      {t('partners.stCreditBase', { currency: statement!.baseCurrency })}
                    </TableHead>
                  </>
                ) : null}
                <TableHead className="text-end">{t('partners.stBalance')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/*
                §5f: "Two special rows bracket the ledger. An OPENING row at
                the top, tinted blue, spanning the first three columns with
                its label, showing dashes in both money columns and the
                opening balance at the end."

                ERR-CRM-04-01 is why it is a row rather than a caption:
                "Searched for a document that is not on the account: the
                ledger body EMPTIES, and THE OPENING AND CLOSING ROWS STAY."
                A statement filtered down to nothing still has to say what
                the account was worth at both ends.
              */}
              <TableRow className="bg-blue-50/60 dark:bg-blue-950/40">
                <TableCell colSpan={3} className="font-medium">
                  {t('partners.stOpening')}
                </TableCell>
                <TableCell />
                <TableCell className="text-end text-muted-foreground" aria-hidden>
                  —
                </TableCell>
                <TableCell className="text-end text-muted-foreground" aria-hidden>
                  —
                </TableCell>
                {converted ? (
                  <>
                    <TableCell />
                    <TableCell />
                  </>
                ) : null}
                <TableCell className="text-end font-semibold">
                  <Amount value={statement?.summary?.openingBalance ?? '0.00'} />
                </TableCell>
              </TableRow>
              {statement && statement.rows.length === 0 ? (
                /*
                 * ERR-CRM-04-02, THE SPEC'S OWN SENTENCE: "No transactions in
                 * the selected period."
                 *
                 * It used to read "No posted documents yet. Drafts never
                 * appear here." — a deliberate deviation, because the message
                 * named a period selector that did not exist and would have
                 * been false. The selector is two cards up now, so the
                 * deviation has nothing left to justify it and the sentence
                 * goes back to the spec's wording, which is also the one
                 * `spec-wording.spec.ts` can pin.
                 */
                <TableRow>
                  <TableCell
                    colSpan={converted ? 9 : 7}
                    className="py-6 text-center text-sm text-muted-foreground"
                  >
                    {t('partners.stNoRows')}
                  </TableCell>
                </TableRow>
              ) : null}
              {(statement?.rows ?? []).map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Ltr value={row.date} className="font-mono" />
                  </TableCell>
                  <TableCell>{t(`partners.stKind.${row.kind}`)}</TableCell>
                  <TableCell>
                    {/* BR-CRM-04-15 — the code itself is the link, to the
                        row's own editor route by kind: the purchase side for
                        a purchase document, the sales side otherwise. */}
                    <Link to={statementDocPath(row)} className="underline-offset-2 hover:underline">
                      <Ltr value={row.code} className="font-mono" />
                    </Link>
                  </TableCell>
                  {/* §5f column 4 — free text from the document, and the
                      other half of what the search box matches. */}
                  <TableCell className="max-w-64 truncate text-muted-foreground">
                    {row.reference ?? ''}
                  </TableCell>
                  {/* BR-CRM-04-12: a row carries either a debit or a
                      credit, never both — the EMPTY column shows an
                      em-dash in a lighter colour. Which side is empty is
                      the row's KIND (an invoice debits the customer; a bill
                      credits the supplier), not a zero-test, so a
                      zero-amount document still dashes the right side. */}
                  <TableCell className="text-end">
                    {isDebitRow(row.kind) ? (
                      <Amount value={row.debit} />
                    ) : (
                      <span aria-hidden className="text-muted-foreground">
                        —
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-end">
                    {isDebitRow(row.kind) ? (
                      <span aria-hidden className="text-muted-foreground">
                        —
                      </span>
                    ) : (
                      <Amount value={row.credit} />
                    )}
                  </TableCell>
                  {converted ? (
                    <>
                      {/* EC-19-10 on a statement: each row at the rate ITS
                          OWN document froze. A row whose document predates
                          the freeze has no rate to convert at, and says so
                          rather than borrowing today's. The side the row
                          does not carry dashes exactly like its own-
                          currency column (BR-CRM-04-12). */}
                      <TableCell className="text-end">
                        {!isDebitRow(row.kind) ? (
                          <span aria-hidden className="text-muted-foreground">
                            —
                          </span>
                        ) : row.debitBase === null ? (
                          <span className="text-muted-foreground" title={t('partners.stNoRate')}>
                            —
                          </span>
                        ) : (
                          <Amount value={row.debitBase} currency={statement!.baseCurrency} />
                        )}
                      </TableCell>
                      <TableCell className="text-end">
                        {isDebitRow(row.kind) ? (
                          <span aria-hidden className="text-muted-foreground">
                            —
                          </span>
                        ) : row.creditBase === null ? (
                          <span className="text-muted-foreground" title={t('partners.stNoRate')}>
                            —
                          </span>
                        ) : (
                          <Amount value={row.creditBase} currency={statement!.baseCurrency} />
                        )}
                      </TableCell>
                    </>
                  ) : null}
                  <TableCell className="text-end">
                    {/*
                      §5g, the sentence this whole screen turns on: the
                      running balance is "the true running balance IN THE
                      CONTEXT OF THE WHOLE STATEMENT — not a balance
                      recomputed from the visible rows". It is the server's
                      figure, carried on the row, and the search never
                      touches it.
                    */}
                    <Amount value={row.balance} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              {/* §5f's closing bracket row — "on a grey band with a heavy top
                  border, its label in bold and its figure larger and in the
                  emphasis colour". BR-CRM-04-06's figure, from the server. */}
              <TableRow>
                <TableCell colSpan={converted ? 8 : 6} className="font-bold">
                  {t('partners.stClosing')}
                </TableCell>
                <TableCell className="text-end text-base font-bold text-primary">
                  <Amount value={statement?.summary?.closingBalance ?? '0.00'} currency={currency} />
                </TableCell>
              </TableRow>
              {converted ? (
                <TableRow>
                  <TableCell colSpan={8} className="font-medium">
                    {t('partners.stClosingBase', { currency: statement!.baseCurrency })}
                  </TableCell>
                  <TableCell className="text-end font-semibold">
                    {/* THE SERVER'S FIGURE, never a browser sum and never
                        the closing balance converted once at today's rate.
                        Those are different numbers whenever the rate moved
                        during the period, and only one of them agrees with
                        what the books carry. Null when some row has no
                        frozen rate: a total that silently omits rows is
                        worse than no total. */}
                    {statement!.closingBalanceBase === null ? (
                      <span className="text-muted-foreground" title={t('partners.stNoRate')}>
                        —
                      </span>
                    ) : (
                      <Amount
                        value={statement!.closingBalanceBase}
                        currency={statement!.baseCurrency}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableFooter>
          </Table>
          <div className="print:hidden">
            <Pager
              page={page}
              pages={range.pages}
              first={range.first}
              last={range.last}
              total={range.total}
              onPage={setPage}
              label={t('partners.stLedger')}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
